import { useState, useEffect } from "react";
import "./OfficerCardLayout.css";
import { useTranslation } from "react-i18next";
import { Spin, Row, Col, Alert, Space, Button } from "antd";
import EmptyState from './EmptyState';
import axiosInstance from "../services/axios";
import Heading from "./Heading";

// Images
import whatsappIcon from "../assets/icons/whatsapp.svg";
import phoneIcon from "../assets/icons/phone.svg";
import { Link } from "react-router-dom";

const getImageUrl = (image, defaultFolder = 'officers') => {
  if (!image) return null;
  if (typeof image !== 'string') return null;

  if (image.startsWith('http://') || image.startsWith('https://')) {
    if (image.includes('localhost') || image.includes('127.0.0.1')) {
      const mediaIdx = image.indexOf('/media/');
      if (mediaIdx !== -1) return image.substring(mediaIdx);
    }
    if (window.location.protocol === 'https:' && image.startsWith('http:')) {
      return image.replace(/^http:/, 'https:');
    }
    return image;
  }

  if (image.startsWith('/media/')) return image;
  if (image.startsWith('/')) return `/media${image}`;
  if (image.includes('/')) return `/media/${image}`;
  return `/media/${defaultFolder}/${image}`;
};

const OfficerCardLayout = ({ className = "" }) => {
  const { t, i18n } = useTranslation();
  const [officers, setOfficers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const languageParam = i18n.language === "mr" ? "Marathi" : "English";
    setLoading(true);

    axiosInstance
      .get("/officers", {
        params: { tags: "officebearer", language: languageParam },
      })
      .then((resp) => {
        if (!isMounted) return;
        setError(null);
        let data = resp?.data || [];
        if (!Array.isArray(data))
          data = data.results || data.data || Object.values(data) || [];

        if (!Array.isArray(data) || data.length === 0) {
          setOfficers([]);
          return;
        }

        const orderKey = ["order", "sort_order", "position"].find((k) =>
          data.every((item) => item[k] !== undefined)
        );

        const ordered = orderKey
          ? [...data].sort(
            (a, b) => (Number(a[orderKey]) || 0) - (Number(b[orderKey]) || 0)
          )
          : data;

        const mapped = ordered.map((d) => ({
          id: d.id,
          name: d.name || "-",
          title: d.designation || d.title || "-",
          imageUrl: getImageUrl(d.image || d.imageUrl, 'officers'),
          contact: d.contact || null,
          whatsapp: d.whatsapp || null,
        }));

        setOfficers(mapped);
      })
      .catch((err) => {
        console.error("Failed to load officers:", err);
        if (isMounted) {
          setOfficers([]);
          setError(t("officers.loadError") || "Failed to load officers");
        }
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [i18n.language, t]);

  return (
    <div>
      <div className={`officer-card-layout ${className}`.trim()}>
        {/* Header */}
        <div className="flex justify-between items-center my-2.5">
          <Heading title={t("officers.title")} />
          <Link to={"/gram-panchayat#officer"}>
            <button className="officer-see-all-button max-sm-hidden">
              {t("officers.seeAllButton")}
            </button>
          </Link>
        </div>

        {/* Officer Cards */}
        <Row
          gutter={[16, 16]}
          style={{ width: "100%", marginLeft: 0, marginRight: 0 }}
        >
          {loading ? (
            <Col span={24}>
              <div style={{ textAlign: "center" }}>
                <Spin />
              </div>
            </Col>
          ) : error ? (
            <Col span={24}>
              <Alert type="warning" message={error} />
            </Col>
          ) : officers.length === 0 ? (
            <Col span={24}>
              <div style={{ padding: '50px 0' }}>
                <EmptyState description={i18n.language === 'mr' ? 'डेटा लवकरच उपलब्ध होईल' : 'Data will be available soon'} />
              </div>
            </Col>
          ) : (
            officers.slice(0, 4).map((officer, index) => {
              const sanitizeNumber = (num) => {
                if (!num) return null;
                // remove spaces, parentheses, dashes, plus signs
                return String(num).replace(/[^0-9]/g, "");
              };

              const phone = sanitizeNumber(
                officer.contact || officer.whatsapp || ""
              );
              const waNumber = sanitizeNumber(
                officer.whatsapp || officer.contact || ""
              );
              const waMessage = encodeURIComponent(
                t("officers.waMessage") || "Hello"
              );

              return (
                <Col
                  className=""
                  xs={24}
                  sm={12}
                  md={12}
                  lg={6}
                  key={officer.id || index}
                >
                  <div className="border border-[#F3F3F3] rounded-md bg-[linear-gradient(179.95deg,#EDF7E6_0.04%,#FFFFFF_80.58%)] bg-no-repeat">


                    {officer.imageUrl && (
                      <img
                        className="officer-image"
                        alt={officer.name}
                        src={officer.imageUrl}
                        style={{ width: "100%" }}
                      />
                    )}
                    <div className="officer-info">
                      <h4 className="officer-name">{officer.name}</h4>
                      <p className="officer-title-text">{officer.title}</p>
                    </div>
                    <Space
                      direction="vertical"
                      align="center"
                      style={{
                        width: "100%",
                        display: "block",
                        padding: "0 1rem",
                        paddingBottom: "1rem",
                      }}
                    >
                      <Button
                        className="officer-contact-button w-full flex items-center justify-center gap-2 text-sm font-medium text-white rounded-md transition"
                        style={{
                          backgroundColor: "var(--color-primary)",
                          border: "none",
                          color: "#fff",
                          width: "100%",
                        }}
                        href={
                          waNumber
                            ? `https://wa.me/${waNumber}?text=${waMessage}`
                            : undefined
                        }
                        target={waNumber ? "_blank" : undefined}
                        rel={waNumber ? "noopener noreferrer" : undefined}
                        disabled={!waNumber}
                        aria-label={
                          waNumber
                            ? `WhatsApp ${officer.name}`
                            : `No WhatsApp number`
                        }
                      >
                        <img loading="lazy" src={whatsappIcon} alt="WhatsApp Icon" />
                        {officer.whatsapp || "WhatsApp"}
                      </Button>
                      <Button
                        className="w-full flex items-center justify-center gap-2 text-sm font-medium border rounded-md transition mt-2"
                        style={{
                          backgroundColor: "#fff",
                          border: "1px solid var(--color-primary)",
                          color: "var(--color-primary)",
                          width: "100%",
                          hover: {
                            backgroundColor: "var(--color-primary-hover)",
                            color: "#fff",
                          },
                        }}
                        href={phone ? `tel:${phone}` : undefined}
                        disabled={!phone}
                        aria-label={
                          phone ? `Call ${officer.name}` : `No phone number`
                        }
                      >
                        <div style={{
                          width: 14,
                          height: 14,
                          display: 'inline-block',
                          WebkitMaskImage: `url(${phoneIcon})`,
                          maskImage: `url(${phoneIcon})`,
                          WebkitMaskSize: 'contain',
                          maskSize: 'contain',
                          WebkitMaskRepeat: 'no-repeat',
                          maskRepeat: 'no-repeat',
                          WebkitMaskPosition: 'center',
                          maskPosition: 'center',
                          backgroundColor: 'var(--color-primary)',
                        }} />
                        {officer.contact || "Call"}
                      </Button>
                    </Space>
                  </div>
                </Col>
              );
            })
          )}
        </Row>

        {/* Mobile button */}
        {/* <button className="officer-see-all-button max-sm-block officer-hidden officer-mt-6 officer-w-full officer-max-w-sm">
          {t("officers.seeAllButton")}
        </button> */}
      </div>
    </div>
  );
};

export default OfficerCardLayout;
